import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/ems/DashboardLayout';
import { dashboardApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import ParticipantDashboard from './ParticipantDashboard';
import { 
  PieChart as PieChartIcon,
  BarChart3,
  Target, 
  Activity, 
  MessageSquare, 
  TrendingUp, 
  Clock,
  ChevronRight,
  PlayCircle,
  PauseCircle,
  CheckCircle2,
  AlertCircle,
  Building2,
  Building,
  FileText
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend
} from 'recharts';

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#6b7280', '#ef4444', '#8b5cf6'];
const STATUS_COLORS = {
  active: '#10b981',
  completed: '#3b82f6',
  planned: '#f59e0b',
  draft: '#6b7280',
  ongoing: '#10b981',
  high: '#ef4444',
  medium: '#f59e0b',
  low: '#10b981',
};

export default function DashboardPage() {
  const { isParticipant, isAdmin, user } = useAuth();
  const navigate = useNavigate();
  const isParticipantView = isParticipant();
  const isAdminView = isAdmin();
  const isSimcelView = !isAdminView && (user?.roles?.includes('simcel') || user?.roles?.includes('controller'));
  const isObserverView = !isAdminView && !isSimcelView && (user?.roles?.includes('observer'));
  const isRestrictedView = isObserverView;
  const statsGridCols = isAdminView ? 'lg:grid-cols-6' : isRestrictedView ? 'lg:grid-cols-3' : 'lg:grid-cols-4';

  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [recentExercises, setRecentExercises] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isParticipantView) loadDashboardData();
  }, [isParticipantView]);

  // Participant gets a completely different dashboard
  if (isParticipantView) return <ParticipantDashboard />;

  const loadDashboardData = async () => {
    try {
      const [statsData, exercisesData, chartsData] = await Promise.all([
        dashboardApi.getStats(),
        dashboardApi.getRecentExercises(5),
        dashboardApi.getCharts().catch(() => null)
      ]);

      setStats(statsData);
      setCharts(chartsData);

      if (isParticipantView) {
        const activeOnly = (exercisesData || []).filter(e => e.status === 'active');
        const displayExercises = activeOnly.length > 0 ? activeOnly : (exercisesData || []);
        setRecentExercises(displayExercises);
      } else if (isObserverView) {
        // Observer only sees assigned exercises — no filter needed, backend already does it
        setRecentExercises(exercisesData);
      } else {
        setRecentExercises(exercisesData);
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <PlayCircle className="w-4 h-4 text-green-500" />;
      case 'planned':
        return <PauseCircle className="w-4 h-4 text-yellow-500" />;
      case 'completed':
        return <CheckCircle2 className="w-4 h-4 text-blue-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'active':
        return 'status-live';
      case 'planned':
        return 'status-paused';
      case 'completed':
        return 'status-completed';
      default:
        return 'status-draft';
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Dashboard">
      <div className="space-y-6">
        {/* Stats Grid */}
        {stats && (
          <div className={`grid grid-cols-2 md:grid-cols-3 ${statsGridCols} gap-4`} data-testid="stats-grid">
            {/* Total Exercises */}
            <Card className="tactical-border bg-card/50 backdrop-blur hover:border-primary/30 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Target className="w-5 h-5 text-primary" />
                  </div>
                </div>
                <p className="font-secondary font-bold text-2xl">{stats?.exercises?.total || 0}</p>
                <p className="font-mono text-xs text-muted-foreground uppercase">Exercises</p>
              </CardContent>
            </Card>

            {/* Active Exercises */}
            <Card className="tactical-border bg-card/50 backdrop-blur hover:border-green-500/30 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                    <PlayCircle className="w-5 h-5 text-green-500" />
                  </div>
                </div>
                <p className="font-secondary font-bold text-2xl text-green-500">{stats?.exercises?.active || 0}</p>
                <p className="font-mono text-xs text-muted-foreground uppercase">Active</p>
              </CardContent>
            </Card>

            {/* Completed */}
            <Card className="tactical-border bg-card/50 backdrop-blur hover:border-blue-500/30 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-blue-500" />
                  </div>
                </div>
                <p className="font-secondary font-bold text-2xl text-blue-500">{stats?.exercises?.completed || 0}</p>
                <p className="font-mono text-xs text-muted-foreground uppercase">Completed</p>
              </CardContent>
            </Card>

            {/* Draft */}
            {!isRestrictedView && (
              <Card className="tactical-border bg-card/50 backdrop-blur hover:border-gray-500/30 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-10 h-10 rounded-lg bg-muted0/10 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-muted-foreground" />
                    </div>
                  </div>
                  <p className="font-secondary font-bold text-2xl text-muted-foreground">{stats?.exercises?.draft || 0}</p>
                  <p className="font-mono text-xs text-muted-foreground uppercase">Draft</p>
                </CardContent>
              </Card>
            )}

            {isAdminView && (
              <>
                {/* Companies */}
                <Card className="tactical-border bg-card/50 backdrop-blur hover:border-purple-500/30 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-purple-500" />
                      </div>
                    </div>
                    <p className="font-secondary font-bold text-2xl text-purple-500">{stats?.companies?.total_clients || 0}</p>
                    <p className="font-mono text-xs text-muted-foreground uppercase">Clients</p>
                  </CardContent>
                </Card>

                {/* Sub-Companies */}
                <Card className="tactical-border bg-card/50 backdrop-blur hover:border-orange-500/30 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
                        <Building className="w-5 h-5 text-orange-500" />
                      </div>
                    </div>
                    <p className="font-secondary font-bold text-2xl text-orange-500">{stats?.companies?.total_sub_companies || 0}</p>
                    <p className="font-mono text-xs text-muted-foreground uppercase">Sub-Companies</p>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        )}

        {/* Charts Row */}
        {charts && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Exercise Status Pie Chart */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="font-secondary text-base flex items-center gap-2">
                  <PieChartIcon className="w-4 h-4" />
                  Exercise Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={charts.exercise_status || []}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {(charts.exercise_status || []).map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={STATUS_COLORS[entry.name] || COLORS[index % COLORS.length]} 
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend 
                      layout="horizontal" 
                      verticalAlign="bottom" 
                      align="center"
                      formatter={(value) => <span className="text-xs capitalize">{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Inject Status Bar Chart */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="font-secondary text-base flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  Inject Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={charts.inject_status || []} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={70} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                      {(charts.inject_status || []).map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={STATUS_COLORS[entry.name] || COLORS[index % COLORS.length]} 
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Inject Priority Distribution */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="font-secondary text-base flex items-center gap-2">
                  <Activity className="w-4 h-4" />
                  Inject Priority
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={charts.inject_priority || []}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      {(charts.inject_priority || []).map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={STATUS_COLORS[entry.name] || COLORS[index % COLORS.length]} 
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Activity Timeline Chart */}
        {charts && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="font-secondary text-base flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Activity (Last 14 Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={charts.activity_timeline || []}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="responses" 
                    stroke="#10b981" 
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    name="Responses"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="injects" 
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    name="Injects Triggered"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Second Row Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Total Injects */}
            <Card className="tactical-border bg-card/50 backdrop-blur">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-mono text-xs text-muted-foreground uppercase">Total Injects</p>
                    <p className="font-secondary font-bold text-2xl mt-1">{stats?.injects?.total || 0}</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                    <Activity className="w-5 h-5 text-purple-500" />
                  </div>
                </div>
                <div className="mt-3">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-muted-foreground">Completed</span>
                    <span className="font-mono">{stats?.injects?.completed || 0}/{stats?.injects?.total || 0}</span>
                  </div>
                  <Progress 
                    value={stats?.injects?.total ? (stats.injects.completed / stats.injects.total) * 100 : 0} 
                    className="h-1.5"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Response Rate */}
            <Card className="tactical-border bg-card/50 backdrop-blur">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-mono text-xs text-muted-foreground uppercase">Response Rate</p>
                    <p className="font-secondary font-bold text-2xl mt-1">{stats?.responses?.response_rate || 0}%</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-green-500" />
                  </div>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  {stats?.responses?.total || 0} total responses
                </p>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card className="tactical-border bg-card/50 backdrop-blur">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-mono text-xs text-muted-foreground uppercase">Last 7 Days</p>
                    <p className="font-secondary font-bold text-2xl mt-1">{stats?.recent_activity?.responses_7d || 0}</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-blue-500" />
                  </div>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  {stats?.recent_activity?.injects_triggered_7d || 0} injects triggered
                </p>
              </CardContent>
            </Card>

            {/* Ongoing Injects */}
            <Card className="tactical-border bg-card/50 backdrop-blur">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-mono text-xs text-muted-foreground uppercase">Ongoing</p>
                    <p className="font-secondary font-bold text-2xl mt-1 text-yellow-500">{stats?.injects?.ongoing || 0}</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                    <PlayCircle className="w-5 h-5 text-yellow-500" />
                  </div>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  Awaiting completion
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Recent Exercises & Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Exercises */}
          <Card className="lg:col-span-2" data-testid="recent-exercises">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-secondary text-lg tracking-tight">Recent Exercises</CardTitle>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/exercises">
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              {recentExercises.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Target className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No exercises yet</p>
                  {!isRestrictedView && (
                    <Button asChild className="mt-4">
                      <Link to="/exercises">Create your first exercise</Link>
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {recentExercises.map((exercise) => (
                    <Link
                      key={exercise.exercise_id}
                      to={`/exercises/${exercise.exercise_id}`}
                      className="flex items-center justify-between p-4 rounded-lg border border-border/50 hover:border-primary/30 hover:bg-accent/50 transition-all group"
                    >
                      <div className="flex items-center gap-4">
                        {getStatusIcon(exercise.status)}
                        <div>
                          <p className="font-medium group-hover:text-primary transition-colors">
                            {exercise.name}
                          </p>
                          <p className="text-sm text-muted-foreground capitalize">
                            {exercise.exercise_type} Exercise
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge 
                          variant="outline" 
                          className={`${getStatusBadgeClass(exercise.status)} border capitalize`}
                        >
                          {exercise.status}
                        </Badge>
                        <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          {!isParticipantView && (
            <Card data-testid="quick-actions">
              <CardHeader>
                <CardTitle className="font-secondary text-lg tracking-tight">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button asChild className="w-full justify-start" variant="outline">
                  <Link to="/exercises">
                    <Target className="w-4 h-4 mr-2" />
                    New Exercise
                  </Link>
                </Button>
                <Button asChild className="w-full justify-start" variant="outline">
                  <Link to="/injects">
                    <Activity className="w-4 h-4 mr-2" />
                    Manage Injects
                  </Link>
                </Button>
                {!isSimcelView && (
                  <Button asChild className="w-full justify-start" variant="outline">
                    <Link to="/time-tracking">
                      <Clock className="w-4 h-4 mr-2" />
                      Log Time
                    </Link>
                  </Button>
                )}
                {!isSimcelView && (
                  <Button asChild className="w-full justify-start" variant="outline">
                    <Link to="/reports">
                      <TrendingUp className="w-4 h-4 mr-2" />
                      View Reports
                    </Link>
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Live Exercises Alert */}
        {!isParticipantView && stats?.exercises?.active > 0 && (
          <Card className="border-green-500/50 bg-green-500/5" data-testid="live-alert">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                <span className="font-medium">
                  {stats.exercises.active} exercise{stats.exercises.active > 1 ? 's' : ''} currently active
                </span>
              </div>
              <Button size="sm" asChild>
                <Link to="/exercises?status=active">
                  View Active Exercises
                </Link>
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
